import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  type ForwardRefExoticComponent,
  type Ref,
  type RefAttributes,
} from 'react'
import { Box, useTheme } from '@dhl-ui/components'
import { festiveLooks, type Festive, type Foreground } from './festives'
import { illustrationColors } from './festives/shared'

export type DeliveryVanProps = {
  /**
   * Height in pixels. The scene scales proportionally. Defaults to 168.
   * DeliveryVan height must be finite positive numbers
   */
  height?: number

  /**
   * The Festive variant: what lies on the roof and what falls in front. Defaults to 'standard',
   * the van without a festive.
   */
  festive?: Festive
}

export type DeliveryVanHandle = {
  /**
   * Start our DHL van
   */
  start: () => void

  /**
   * Brakes and resolves once the van stands still.
   */
  stop: () => Promise<void>
}

// drawing units, y 54-292
const sceneHeight = 238
const backgroundTileWidth = 520
// middle of the van body
const vanCenterX = 268
// drawing units per second while cruising
const cruiseSpeed = 255
// seconds from standstill to cruising, and from cruising to standstill
const accelerationTime = 1.1
const brakingTime = 1.1
// the background moves slower than the road, the falling foreground almost as fast
const backgroundRate = 0.6
const foregroundRate = 0.9
const wheelRadius = 26
const wheelCenters = [195, 378]
const wheelCenterY = 245.5
// after a pause (a hidden tab) nothing jumps ahead
const maxFrameTime = 1 / 30

// the body leans back when pulling away and dips its nose when braking: degrees
const lean: Spring = { size: 1.2, stiffness: 160, damping: 21 }
// the van lags behind when pulling away and lurches forward when braking: drawing units
const shift: Spring = { size: 5, stiffness: 90, damping: 16 }

// the falling snow, leaves and petals: one tile that repeats
const foregroundTileWidth = 240
const foregroundTileHeight = 180
// seconds for one sway to the left and back
const swayTime = 2.8
// seconds to fade in as the van pulls away, and to fall on and fade out once it stands still
const foregroundFadeInTime = 0.6
const foregroundFadeOutTime = 1

const format = (value: number) => value.toFixed(2)
const imageUrl = (svg: string) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`

/*
 * One tile that repeats. The far shapes are a 56% group inside the 9% group, so they read
 * as 5%, and no overlap gets darker. It is the same for every season.
 */
const distantScenery = [
  '<path d="M0 144C130 143 230 136 330 135.2C420 134.4 480 144 520 144V180H0Z"/>',
  '<path d="M10.5 121L47.5 96L84.5 121V150H10.5Z"/>',
  '<circle cx="425" cy="135" r="15"/><circle cx="451" cy="128" r="23"/>',
  '<circle cx="470" cy="136" r="13"/><rect x="412" y="132" width="70" height="18"/>',
].join('')

const nearScenery = [
  '<circle cx="58" cy="163" r="16"/><circle cx="85" cy="152" r="22"/>',
  '<circle cx="105" cy="162" r="14"/><rect x="44" y="160" width="74" height="16"/>',
  '<path d="M195.25 117L226 96.5L256.75 117V176H195.25Z"/>',
  '<rect x="265.5" y="80.5" width="50" height="120" rx="25"/>',
  '<ellipse cx="375.5" cy="96.75" rx="29.5" ry="36.25"/>',
  '<rect x="371.8" y="120" width="6.6" height="56"/>',
].join('')

const groundPath = 'M0 166C60 165.5 90 164 130 164C220 164 320 170.5 400 170.2C450 170 485 166 520 166V292H0Z'

// the ground fades out towards the bottom
const groundFade = [
  [0, 1],
  [0.3, 1],
  [0.77, 0.6],
  [0.86, 0.48],
  [1, 0],
]

const backgroundTileUrl = (color: string, festiveBackground = '') => {
  const stops = groundFade
    .map(([offset, opacity]) => `<stop offset="${offset}" stop-color="${color}" stop-opacity="${opacity}"/>`)
    .join('')
  return imageUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 54 ${backgroundTileWidth} ${sceneHeight}"` +
      ` preserveAspectRatio="none"><defs><linearGradient id="g" x1="0" y1="160" x2="0" y2="292"` +
      ` gradientUnits="userSpaceOnUse">${stops}</linearGradient></defs>` +
      `<g opacity=".09" fill="${color}"><g opacity=".56">${distantScenery}</g>${nearScenery}` +
      `<path fill="url(#g)" d="${groundPath}"/></g>${festiveBackground}</svg>`
  )
}

/**
 * OUR VAN
 */
const vanPaths = {
  cab:
    'M278.5 218.5V131.5H311Q322.5 131.5 330.5 138.6L370 173Q375.5 177.6 383 179' +
    'L398 182.6Q417.5 186.8 417.5 204V218.5Z',
  window: 'M287.9 146.5H326.6Q329.6 146.5 331.6 148.6L363.3 177.5H293.3Z',
  seatAndMirror:
    'M289.5 156.5Q289.6 152.6 292.3 152.6Q294.8 152.6 295.5 155.6L301.6 177.5H293.3Z' +
    'M330 177.5L354.8 169.8L363.3 177.5Z',
  handle: 'M296.4 193.2C296.4 190.4 298.4 188.8 301 188.8C303.6 188.8 305.6 190.4 305.6 193.2Z',
  logoD: 'M150 131.6H165.4Q169.8 131.6 169.4 135.6L169 139Q168.7 141.6 166 141.6H154.2V135H150Z',
}

// the rest of the DHL logo, as x, y, width, height: the H, the L and the speed lines
const logoBars = [
  [171.4, 131.6, 6.4, 10],
  [181, 131.6, 6.7, 10],
  [171.4, 134.9, 16.3, 2.2],
  [189.8, 131.6, 6.9, 10],
  [189.8, 137.7, 15, 3.9],
  [143.4, 138.3, 9.2, 1.1],
  [143.8, 140.1, 9.2, 1.5],
  [206.4, 138.3, 9, 1.2],
  [206.2, 140.1, 9.2, 1.5],
]
// the openings in the letters
const logoGaps = [
  [160.6, 134.9, 2.8, 2.9],
  [154.2, 134.85, 6.4, 0.7],
  [163.4, 137.05, 45, 0.7],
]

const Bars = ({ bars }: { bars: number[][] }) => (
  <>
    {bars.map(([x, y, width, height]) => (
      <rect key={`${x} ${y}`} x={x} y={y} width={width} height={height} />
    ))}
  </>
)

// the motion blur on a wheel: an arc on its tyre
const wheelArc = (centerX: number, startAngle: number, endAngle: number) => {
  const point = (angle: number) => {
    const radians = (angle * Math.PI) / 180
    const x = centerX + 21 * Math.cos(radians)
    const y = wheelCenterY + 21 * Math.sin(radians)
    return `${format(x)} ${format(y)}`
  }

  return `M${point(startAngle)}A21 21 0 0 1 ${point(endAngle)}`
}

const leafPath = 'M0-6C3.4-4.4 5-1.2 4.2 1.8C3.4 4.2 1.4 5.4 0 6C-1.4 5.4-3.4 4.2-4.2 1.8C-5-1.2-3.4-4.4 0-6Z'

/*
 * Foreground: one tile of falling particles in front of the van, repeated both ways. Near
 * (big) and far (small, fainter) particles share the tile, so one layer gives depth. It only
 * moves with transform, so the GPU moves it.
 */
const particles: Record<Foreground['shape'], (size: number, color: string, edge: string) => string> = {
  snowflake: (size, color, edge) => `<circle r="${format(size)}" fill="${color}" stroke="${edge}" stroke-width=".7"/>`,
  leaf: (size, color) => `<path transform="scale(${(size / 6).toFixed(3)})" fill="${color}" d="${leafPath}"/>`,
  petal: (size, color) => `<ellipse rx="${format(size)}" ry="${format(size * 0.58)}" fill="${color}"/>`,
}

const foregroundTileUrl = (foreground: Foreground) => {
  const { shape, colors, edgeColor, size, columns, rows } = foreground
  // the same "random" numbers every time, so the tile never changes
  let seed = 31

  const random = () => {
    seed = (seed * 16807) % 2147483647
    return (seed - 1) / 2147483646
  }

  const cellWidth = foregroundTileWidth / columns
  const cellHeight = foregroundTileHeight / rows

  // keeps every particle inside its cell, so none is cut at the tile edge
  const margin = size * 1.6
  const drawn: string[] = []

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const near = (row + column) % 2 === 0
      const x = column * cellWidth + margin + random() * (cellWidth - 2 * margin)
      const y = row * cellHeight + margin + random() * (cellHeight - 2 * margin)
      const particleSize = size * (near ? 0.85 + 0.3 * random() : 0.45 + 0.15 * random())
      const angle = Math.round(random() * 360)
      const particle = particles[shape](particleSize, colors[drawn.length % colors.length], edgeColor)
      const place = `translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${angle})`

      drawn.push(`<g transform="${place}" opacity="${near ? 1 : 0.6}">${particle}</g>`)
    }
  }

  const viewBox = `0 0 ${foregroundTileWidth} ${foregroundTileHeight}`

  return imageUrl(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}">${drawn.join('')}</svg>`)
}

type Spring = { size: number; stiffness: number; damping: number }
// where a spring is and how fast it moves
type SpringState = { value: number; velocity: number }

export type Motion = {
  driving: boolean
  // 0 standing still, 1 cruising; the speed follows it on an eased curve
  throttle: number
  velocity: number
  travel: number
  // seconds the snow, leaves or petals have been falling
  sceneTime: number
  // 0 to 1: they fade in as the van pulls away, and fall on and fade out once it stands still
  foregroundOpacity: number
  // degrees the body leans back or forward
  pitch: SpringState
  // drawing units the van lags behind or lurches forward
  offset: SpringState
}

// slow at the start and the end, fastest (1.5) halfway
const easeInOut = (progress: number) => progress * progress * (3 - 2 * progress)
const clamp = (value: number) => Math.min(1, Math.max(0, value))
// the steepest change of speed, at half throttle
const hardestAcceleration = (1.5 * cruiseSpeed) / accelerationTime

/**
 * Pulls a spring towards size × push, slowed by its damping.
 */
function moveSpring(state: SpringState, spring: Spring, push: number, time: number) {
  const pull = spring.stiffness * (-spring.size * push - state.value)

  // reason: we animate our svg
  // eslint-disable-next-line no-param-reassign
  state.velocity += (pull - spring.damping * state.velocity) * time
  // reason: we animate our svg
  // eslint-disable-next-line no-param-reassign
  state.value += state.velocity * time
}

const springMovement = ({ value, velocity }: SpringState) => Math.abs(value) + Math.abs(velocity)

export const createMotion = (): Motion => ({
  driving: false,
  throttle: 0,
  velocity: 0,
  travel: 0,
  sceneTime: 0,
  foregroundOpacity: 0,
  pitch: { value: 0, velocity: 0 },
  offset: { value: 0, velocity: 0 },
})

export function advanceMotion(motion: Motion, time: number): void {
  const previousVelocity = motion.velocity
  // reason: we animate our svg
  // eslint-disable-next-line no-param-reassign
  motion.throttle += motion.driving ? time / accelerationTime : -time / brakingTime
  // reason: we animate our svg
  // eslint-disable-next-line no-param-reassign
  motion.throttle = clamp(motion.throttle)
  // reason: we animate our svg
  // eslint-disable-next-line no-param-reassign
  motion.velocity = cruiseSpeed * easeInOut(motion.throttle)
  // reason: we animate our svg
  // eslint-disable-next-line no-param-reassign
  motion.travel += motion.velocity * time
  // reason: we animate our svg
  // eslint-disable-next-line no-param-reassign
  motion.sceneTime += time

  // fade in while driving; once the van stands still, fall on and fade out
  if (motion.driving) {
    // reason: we animate our svg
    // eslint-disable-next-line no-param-reassign
    motion.foregroundOpacity += time / foregroundFadeInTime
  } else if (!motion.velocity) {
    // reason: we animate our svg
    // eslint-disable-next-line no-param-reassign
    motion.foregroundOpacity -= time / foregroundFadeOutTime
  }

  // reason: we animate our svg
  // eslint-disable-next-line no-param-reassign
  motion.foregroundOpacity = clamp(motion.foregroundOpacity)

  // 1 at the hardest pull-away, -1 at the hardest braking
  const push = time > 0 ? (motion.velocity - previousVelocity) / time / hardestAcceleration : 0
  moveSpring(motion.pitch, lean, push, time)
  moveSpring(motion.offset, shift, push, time)
}

/**
 * True once the van stands still; the snow, leaves or petals may still be falling.
 */
export const isAtRest = (motion: Motion): boolean =>
  !motion.driving && !motion.velocity && springMovement(motion.pitch) + springMovement(motion.offset) < 0.12

/**
 * True once the van stands still and the snow, leaves or petals have faded out.
 */
export const isSceneAtRest = (motion: Motion): boolean => isAtRest(motion) && !motion.foregroundOpacity

/**
 * Where the foreground tile is, in pixels: it falls, sways and scrolls with the road.
 */
export const foregroundOffset = (foreground: Foreground, motion: Motion, scale: number): { x: number; y: number } => ({
  x:
    foreground.sway * scale * Math.sin((2 * Math.PI * motion.sceneTime) / swayTime) -
    ((motion.travel * foregroundRate * scale) % Math.round(foregroundTileWidth * scale)),
  y: (motion.sceneTime * foreground.fallSpeed * scale) % Math.round(foregroundTileHeight * scale),
})

type SceneElements = {
  background?: HTMLDivElement | null
  van?: SVGGElement | null
  body?: SVGGElement | null
  foreground?: HTMLDivElement | null
  wheels: (SVGGElement | null)[]
  wheelBlurs: (SVGGElement | null)[]
}

type Playback = Motion & {
  frameRequest: number
  lastFrameTime: number
  // stop() calls waiting for the van to stand still
  waitingForRest: (() => void)[]
}

/**
 * The van stands still: put it exactly at rest and let every waiting stop() resolve.
 */
function settle(playback: Playback) {
  // reason: we animate our svg
  // eslint-disable-next-line no-param-reassign
  playback.pitch = { value: 0, velocity: 0 }
  // reason: we animate our svg
  // eslint-disable-next-line no-param-reassign
  playback.offset = { value: 0, velocity: 0 }

  playback.waitingForRest.splice(0).forEach((resolve) => resolve())
}

/**
 * Decorative Delivery van, ref-controlled pull-to-refresh scene.
 * With reduced-motion preferences it stays where it is.
 */
export const DeliveryVan: ForwardRefExoticComponent<DeliveryVanProps & RefAttributes<DeliveryVanHandle>> = forwardRef<
  DeliveryVanHandle,
  DeliveryVanProps
>(({ height = 168, festive = 'default' }, ref: Ref<DeliveryVanHandle>) => {
  const { palette } = useTheme()
  const id = useId().replace(/:/g, '')
  const { roofLoad: RoofLoad, background: festiveBackground, foreground } = festiveLooks[festive]

  const scale = height / sceneHeight
  const tileWidth = Math.max(1, Math.round(backgroundTileWidth * scale))
  const foregroundWidth = Math.round(foregroundTileWidth * scale)
  const foregroundHeight = Math.round(foregroundTileHeight * scale)
  const foregroundMargin = foreground ? Math.ceil(foreground.sway * scale) : 0
  // follows the theme, so the scenery turns light in dark mode
  const sceneryColor = palette.pure.dark

  const backgroundImage = useMemo(
    () => backgroundTileUrl(sceneryColor, festiveBackground),
    [sceneryColor, festiveBackground]
  )
  const foregroundImage = useMemo(() => foreground && foregroundTileUrl(foreground), [foreground])
  const animationConfig = useRef({ scale, tileWidth, foreground })
  const sceneElements = useRef<SceneElements>({ wheels: [], wheelBlurs: [] })
  const sceneAnimations = useRef<{ scrolling: Animation[]; drifting: Animation[] }>({ scrolling: [], drifting: [] })
  const playbackRef = useRef<Playback>({
    ...createMotion(),
    frameRequest: 0,
    lastFrameTime: 0,
    waitingForRest: [],
  })

  function draw() {
    const { van, body, wheelBlurs } = sceneElements.current
    const falling = sceneElements.current.foreground
    const playback = playbackRef.current
    const blur = format(Math.min(1, playback.velocity / 130) * 0.9)
    const rate = playback.velocity / cruiseSpeed
    sceneAnimations.current.scrolling.forEach((animation) => {
      if (animation.playbackRate !== rate) {
        animation.updatePlaybackRate(rate)
      }
    })

    van?.setAttribute('transform', `translate(${format(playback.offset.value)} 0)`)
    body?.setAttribute('transform', `rotate(${format(playback.pitch.value)} 300 245)`)

    wheelBlurs.forEach((wheelBlur) => wheelBlur?.setAttribute('opacity', blur))

    if (falling) {
      // eased, so they fade in and out softly
      falling.style.opacity = format(easeInOut(playback.foregroundOpacity))
    }
  }

  // background and foreground get their own GPU layer only while the van drives
  function setMovingLayers(moving: boolean) {
    const { background, foreground: foregroundLayer } = sceneElements.current
    const elements = [background, foregroundLayer]
    elements.forEach((element) => {
      if (element) {
        element.style.setProperty('will-change', moving ? 'transform' : '')
      }
    })
    const { scrolling, drifting } = sceneAnimations.current
    ;[...scrolling, ...drifting].forEach((animation) => {
      if (moving) {
        animation.play()
      } else {
        animation.pause()
      }
    })
  }

  function tick(now: number) {
    const playback = playbackRef.current
    advanceMotion(playback, Math.min((now - playback.lastFrameTime) / 1000, maxFrameTime))
    playback.lastFrameTime = now

    if (isAtRest(playback)) {
      settle(playback)
    }

    // without snow, leaves or petals the scene rests as soon as the van does
    if (!animationConfig.current.foreground) {
      playback.foregroundOpacity = 0
    }

    draw()

    const sceneAtRest = isSceneAtRest(playback)
    playback.frameRequest = sceneAtRest ? 0 : requestAnimationFrame(tick)

    if (sceneAtRest) {
      setMovingLayers(false)
    }
  }

  useImperativeHandle(ref, () => ({
    start() {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        return
      }

      const playback = playbackRef.current
      playback.driving = true

      if (playback.frameRequest) {
        return
      }

      setMovingLayers(true)

      playback.lastFrameTime = performance.now()
      playback.frameRequest = requestAnimationFrame(tick)
    },
    stop() {
      const playback = playbackRef.current
      playback.driving = false
      if (!playback.frameRequest) {
        return Promise.resolve()
      }
      return new Promise<void>((resolve) => {
        playback.waitingForRest.push(resolve)
      })
    },
  }))

  useEffect(() => {
    const { background, foreground: falling, wheels } = sceneElements.current
    const scrolling: Animation[] = []
    const drifting: Animation[] = []
    const animate = (
      element: Element,
      keyframes: Keyframe[],
      duration: number,
      composite: CompositeOperation = 'replace'
    ) => {
      const animation = element.animate(keyframes, { duration, iterations: Infinity, composite })
      animation.pause()
      animation.currentTime = 0
      return animation
    }

    if (background) {
      scrolling.push(
        animate(
          background,
          [{ transform: 'translate3d(0,0,0)' }, { transform: `translate3d(${-tileWidth}px,0,0)` }],
          (tileWidth / (cruiseSpeed * backgroundRate * scale)) * 1000
        )
      )
    }
    wheels.forEach((wheel) => {
      if (wheel) {
        scrolling.push(
          animate(
            wheel,
            [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
            ((2 * Math.PI * wheelRadius) / cruiseSpeed) * 1000
          )
        )
      }
    })
    if (falling && foreground) {
      scrolling.push(
        animate(
          falling,
          [{ transform: 'translateX(0)' }, { transform: `translateX(${-foregroundWidth}px)` }],
          (foregroundWidth / (cruiseSpeed * foregroundRate * scale)) * 1000
        )
      )
      drifting.push(
        animate(
          falling,
          [{ transform: 'translateY(0)' }, { transform: `translateY(${foregroundHeight}px)` }],
          (foregroundHeight / (foreground.fallSpeed * scale)) * 1000,
          'add'
        ),
        animate(
          falling,
          Array.from({ length: 33 }, (_, index) => ({
            transform: `translateX(${foreground.sway * scale * Math.sin((2 * Math.PI * index) / 32)}px)`,
          })),
          swayTime * 1000,
          'add'
        )
      )
    }
    sceneAnimations.current = { scrolling, drifting }
    draw()
    setMovingLayers(!!playbackRef.current.frameRequest)

    return () => {
      ;[...scrolling, ...drifting].forEach((animation) => animation.cancel())
    }
  }, [scale, tileWidth, foreground, foregroundWidth, foregroundHeight])

  // a new height or season: keep the van where it is and draw it at the new size
  useEffect(() => {
    animationConfig.current = { scale, tileWidth, foreground }
    draw()
  })

  // gone from the page: stop the frames and let a waiting stop() resolve
  useEffect(() => {
    const playback = playbackRef.current
    return () => {
      cancelAnimationFrame(playback.frameRequest)
      Object.assign(playback, { frameRequest: 0, driving: false })
      settle(playback)
    }
  }, [])

  const setSceneElement =
    <Key extends 'background' | 'van' | 'body' | 'foreground'>(key: Key) =>
    (node: SceneElements[Key]) => {
      sceneElements.current[key] = node
    }

  const shadow = `url(#${id}s)`
  const center = (width: number) => `calc(50% - ${format(width * scale)}px)`

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'relative',
        width: '100%',
        height,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <Box
        ref={setSceneElement('background')}
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: -tileWidth,
          right: -tileWidth,
          backgroundImage,
          backgroundRepeat: 'repeat-x',
          backgroundSize: `${tileWidth}px ${height}px`,
          backgroundPosition: `${center(vanCenterX - backgroundTileWidth / 2)} 0`,
        }}
      />

      <svg
        viewBox="-12 54 432 238"
        width={format(432 * scale)}
        height={height}
        style={{
          position: 'absolute',
          bottom: 0,
          left: center(vanCenterX + 12),
          overflow: 'visible',
        }}
      >
        <defs>
          <linearGradient id={`${id}s`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={palette.common.black} stopOpacity="0" />
            <stop offset="1" stopColor={palette.common.black} stopOpacity=".33" />
          </linearGradient>
          <clipPath id={`${id}a`}>
            <rect x="100" y="100" width="340" height="145.5" />
          </clipPath>
        </defs>

        <g ref={setSceneElement('van')}>
          <rect x="-10" y="266.8" width="205" height="4.8" fill={shadow} />
          <rect x="173" y="266.8" width="205" height="4.8" fill={shadow} />

          <g ref={setSceneElement('body')}>
            <rect x="118.5" y="116.5" width="160.5" height="102" fill={illustrationColors.body} />
            <rect x="118.5" y="116.5" width="11" height="102" fill={illustrationColors.dark} />
            <path fill={illustrationColors.body} d={vanPaths.cab} />
            <rect x="278.6" y="131.5" width="2.6" height="87" fill={illustrationColors.dark} />
            <path fill={illustrationColors.trim} d={vanPaths.window} />
            <path fill={palette.common.black} d={vanPaths.seatAndMirror} />
            <path fill={illustrationColors.tyre} d={vanPaths.handle} />

            <g transform="matrix(1 0 -0.75 1 106.2 0)">
              <g fill={illustrationColors.logo}>
                <path d={vanPaths.logoD} />
                <Bars bars={logoBars} />
              </g>
              <g fill={illustrationColors.body}>
                <Bars bars={logoGaps} />
              </g>
            </g>

            <rect x="118.5" y="218.5" width="299" height="27" fill={illustrationColors.trim} />
            <rect x="118.5" y="218.5" width="11" height="27" fill={palette.common.black} />
            <rect x="278.6" y="218.5" width="2.6" height="27" fill={illustrationColors.tyre} />

            <g clipPath={`url(#${id}a)`} fill={palette.common.black}>
              {wheelCenters.map((centerX) => (
                <circle key={centerX} cx={centerX} cy={wheelCenterY} r="30" />
              ))}
            </g>

            {/* the season's layer on the roof, moving with the body */}
            {RoofLoad && <RoofLoad />}
          </g>

          {wheelCenters.map((centerX, index) => (
            <g
              key={centerX}
              style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
              ref={(node) => {
                sceneElements.current.wheels[index] = node
              }}
            >
              <circle cx={centerX} cy={wheelCenterY} r={wheelRadius} fill={illustrationColors.tyre} />
              <g
                ref={(node) => {
                  sceneElements.current.wheelBlurs[index] = node
                }}
                opacity="0"
                fill="none"
                stroke={illustrationColors.blur}
                strokeWidth="3.4"
                strokeLinecap="round"
              >
                <path d={wheelArc(centerX, 205, 255)} />
                <path d={wheelArc(centerX, 25, 75)} />
              </g>
              <circle cx={centerX} cy={wheelCenterY} r="16" fill={illustrationColors.trim} />
              <circle cx={centerX} cy={wheelCenterY} r="8" fill={illustrationColors.hub} />
            </g>
          ))}
        </g>
      </svg>

      {foregroundImage && (
        <Box
          ref={setSceneElement('foreground')}
          sx={{
            position: 'absolute',
            top: -foregroundHeight,
            bottom: 0,
            left: -foregroundWidth - foregroundMargin,
            right: -foregroundWidth - foregroundMargin,
            backgroundImage: foregroundImage,
            backgroundSize: `${foregroundWidth}px ${foregroundHeight}px`,
            // only visible while the van drives; see foregroundOpacity
            opacity: 0,
          }}
        />
      )}
    </div>
  )
})
